import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import {
  Plus,
  Trash2,
  CalendarIcon,
  Paperclip,
  ExternalLink,
  Loader2,
} from "lucide-react";

import { Currency } from "@/components/i18n/Currency";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { Expense } from "@/types/nanny";
import { useFilteredRequests } from "@/hooks/useFilteredRequests";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface ExpensesSectionProps {
  expenses: Expense[];
  submissionId: number;
  isEditable: boolean;
  onUpdate: (expenses: Expense[]) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "application/pdf"];

const expenseSchema = z.object({
  amount: z
    .number()
    .positive("Amount must be greater than 0")
    .max(10000, "Amount cannot exceed £10,000"),
  description: z
    .string()
    .min(5, "Description must be at least 5 characters")
    .max(200, "Description cannot exceed 200 characters"),
  expenseDate: z.date(),
  receipt: z
    .instanceof(FileList)
    .refine(
      (files) => files.length === 0 || files[0].size <= MAX_FILE_SIZE,
      "File size must be less than 5MB",
    )
    .refine(
      (files) => files.length === 0 || ACCEPTED_FILE_TYPES.includes(files[0].type),
      "File type must be JPEG, PNG, or PDF",
    )
    .optional(),
});

type FormValues = z.infer<typeof expenseSchema>;

const ExpensesSection: React.FC<ExpensesSectionProps> = ({
  expenses,
  submissionId,
  isEditable,
  onUpdate,
}) => {
  const { toast } = useToast();
  const { filteredItems, showDeleted, setShowDeleted } =
    useFilteredRequests(expenses);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState<Record<number, string>>({});

  const form = useForm<FormValues>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      amount: undefined,
      description: "",
      expenseDate: new Date(),
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setIsUploading(true);
      
      let receiptUrl: string | null = null;
      
      // Extract the file from the form values
      const fileList = values.receipt;
      
      // Only upload if a file was provided
      if (fileList && fileList.length > 0) {
        const file = fileList[0];
        const fileType = file.type;
        
        console.log("Uploading file of type:", fileType);
        
        // Step 1: Get a presigned URL for the file upload
        interface UploadUrlResponse {
          uploadUrl: string;
          fileKey: string;
          s3Url: string;
        }
        
        const uploadUrlResponse = await ApiService.request<UploadUrlResponse>(
          EndpointKeys.GET_EXPENSE_UPLOAD_URL,
          {
            method: "POST",
            urlParams: { submissionId },
            body: { fileType },
          }
        );
        
        // Step 2: Upload the file directly to S3 with minimal config
        try {
          console.log("Starting S3 upload to:", uploadUrlResponse.uploadUrl);
          
          // For S3 presigned URLs without Content-Type in the signature,
          // we shouldn't send Content-Type in request either to avoid CORS issues
          const uploadResponse = await fetch(uploadUrlResponse.uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": fileType // Send the Content-Type header matching the presigned URL
            },
            // No headers at all - this helps avoid CORS preflight
            mode: 'cors',
            credentials: 'omit' // Critical: Don't send any credentials
          });
          
          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            console.error(`S3 upload failed with status: ${uploadResponse.status} ${uploadResponse.statusText}`);
            console.error("Error details:", errorText);
            throw new Error(`File upload to S3 failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
          }
          
          console.log("Upload successful, S3 URL:", uploadUrlResponse.s3Url);
          receiptUrl = uploadUrlResponse.s3Url;
        } catch (err) {
          console.error("Error during S3 upload:", err);
          throw err;
        }
      }
      
      // Step 3: Create the expense with or without the receipt URL
      const newExpenseResponse = await ApiService.request<{ id: number }>(EndpointKeys.CREATE_EXPENSE, {
        method: "POST",
        urlParams: { submissionId },
        body: {
          amount: values.amount,
          description: values.description,
          workDate: format(values.expenseDate, "yyyy-MM-dd"),
          receipt_url: receiptUrl,
        },
      });

      toast({
        title: "Success",
        description: "Expense added successfully",
      });

      form.reset();
      setIsDialogOpen(false);
      setIsUploading(false);

      // Update local state with the new expense
      const newExpense: Expense = {
        id: newExpenseResponse.id || Date.now(), // Fallback ID if not provided
        amount: values.amount,
        description: values.description,
        effective_date: values.expenseDate,
        receipt_url: receiptUrl || undefined,
        status: 'PENDING' // Default status for new expenses
      };
      
      onUpdate([...expenses, newExpense]);
    } catch (err) {
      setIsUploading(false);
      console.error("Error adding expense:", err);
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to add expense",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await ApiService.request(EndpointKeys.DELETE_EXPENSE, {
        urlParams: { submissionId, id },
        method: "DELETE",
      });

      toast({
        title: "Success",
        description: "Expense deleted successfully",
      });

      // Update local state by removing the deleted expense
      onUpdate(expenses.filter(expense => expense.id !== id));
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to delete expense",
        variant: "destructive",
      });
    }
  };



  // Function to get a presigned download URL if necessary
  const getReceiptDownloadUrl = async (expense: Expense) => {
    if (!expense.receipt_url) return;
    
    // If the URL is already a presigned URL (contains a signature parameter)
    if (expense.receipt_url.includes('X-Amz-Signature=')) {
      return expense.receipt_url;
    }
    
    // If we already have a cached download URL for this expense
    if (downloadUrls[expense.id]) {
      return downloadUrls[expense.id];
    }
    
    try {
      const response = await ApiService.request<{presignedUrl: string}>(
        EndpointKeys.GET_RECEIPT_DOWNLOAD_URL,
        {
          method: "GET",
          urlParams: {},
          queryParams: { receipt_url: expense.receipt_url }
        }
      );
      
      // Cache the presigned URL
      setDownloadUrls(prev => ({
        ...prev,
        [expense.id]: response.presignedUrl
      }));
      
      return response.presignedUrl;
    } catch (err) {
      console.error("Error getting presigned URL:", err);
      return expense.receipt_url; // Fall back to original URL
    }
  };
  
  // Handle clicking on a receipt link
  const handleReceiptClick = async (e: React.MouseEvent, expense: Expense) => {
    e.preventDefault();
    
    try {
      const url = await getReceiptDownloadUrl(expense);
      // Open in a new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error("Error opening receipt:", err);
      toast({
        title: "Error",
        description: "Failed to open receipt. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      {isEditable && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Expense</DialogTitle>
              <DialogDescription>
                Submit an expense with receipt for reimbursement
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="expenseDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 pointer-events-auto"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            defaultMonth={
                              field.value ? new Date(field.value) : undefined
                            }
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Enter the amount in pounds
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Enter a description of the expense"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="receipt"
                  render={({ field: { onChange, value, ...field } }) => (
                    <FormItem>
                      <FormLabel>Receipt</FormLabel>
                      <FormControl>
                        <Input
                          type="file"
                          accept={ACCEPTED_FILE_TYPES.join(",")}
                          onChange={(e) => onChange(e.target.files)}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Upload a receipt (optional - JPEG, PNG, or PDF, max 5MB)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    "Submit Expense"
                  )}
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex justify-between items-center mb-4">
        {isEditable && (
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        )}
        <div className="flex items-center space-x-2">
          <Switch
            id="show-deleted"
            checked={showDeleted}
            onCheckedChange={setShowDeleted}
          />
          <Label htmlFor="show-deleted">Show deleted expenses</Label>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead>Status</TableHead>
              {isEditable && (
                <TableHead className="w-[100px]">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isEditable ? 6 : 5}
                  className="text-center text-muted-foreground"
                >
                  No expenses recorded
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((expense) => (
                <TableRow key={expense.id}>
                  <TableCell>
                    {format(new Date(expense.effective_date), "PPP")}
                  </TableCell>
                  <TableCell>{expense.description}</TableCell>
                  <TableCell><Currency amount={expense.amount} /></TableCell>
                  <TableCell>
                    {expense.receipt_url ? (
                      <a
                        href={expense.receipt_url}
                        onClick={(e) => handleReceiptClick(e, expense)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-500 hover:text-blue-700"
                      >
                        <Paperclip className="h-4 w-4 mr-1" />
                        View
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">No receipt</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "rounded-full px-2 py-1 text-xs font-medium",
                        {
                          "bg-green-100 text-green-800":
                            expense.status === "APPROVED",
                          "bg-yellow-100 text-yellow-800":
                            expense.status === "PENDING",
                          "bg-red-100 text-red-800":
                            expense.status === "REJECTED",
                        },
                      )}
                    >
                      {expense.status}
                    </span>
                  </TableCell>
                  {isEditable && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(expense.id)}
                        className="hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ExpensesSection;
